import {
    useGetAccountInfo,
    sendTransactions,
    logout,
    refreshAccount,
} from '@elrondnetwork/dapp-core'
import LoginComponent from 'components/Connect'
import { getAccountNgmiBalance, getWalletBodies } from 'helpers/asyncRequests'
import React, {
    FormEventHandler,
    MouseEventHandler,
    useEffect,
    useState,
} from 'react'
import fridgeImg from '../../luniImages/blankFridge.jpeg'
import './StakingContent.css'
import {
    bigUint2hex,
    decodeAddress,
    int2hex,
    padBeforeString,
    str2hex,
    strNestedEncode,
} from 'helpers/encoding'
import { nftStakingSCAddress, ngmiStakingSCAddress } from 'config'
import { getNftStakingData } from 'helpers/sqQueries'
import {
    Address,
    AddressValue,
    BigUIntValue,
    U32Value,
} from '@elrondnetwork/erdjs/out'
import Logo from '../Logo'
import internal from 'assert'
import { useModalDisableScroll } from 'helpers/hooks'
import LoginComponentInline from 'components/ConnectInline'
import BigNumber from 'bignumber.js'
import { fetchQueryHex } from 'helpers/scRequests'

interface IFetch {
    isLoading: boolean
    isError: boolean
    data: any
}

interface IUnstake {
    isReady: boolean
    isUnbonding: boolean
    date: Date | null
    amount: number
}

interface IFetchUnstake extends IFetch {
    data: IUnstake | null
}

const defaultFetch = {
    isLoading: true,
    isUnbonding: true,
    isError: false,
    data: null,
}

const useNGMIStakedBalance = (address: string) => {
    const [ngmiStakedBalance, setNgmiStakedBalance] =
        useState<IFetch>(defaultFetch)

    useEffect(() => {
        if (address) {
            fetchQueryHex(
                'stakedAmount',
                [new AddressValue(new Address(address))],
                ngmiStakingSCAddress
            ).then((res) => {
                if (res === '-1') {
                    setNgmiStakedBalance({
                        isLoading: false,
                        isError: false,
                        data: 0,
                    })
                } else {
                    setNgmiStakedBalance({
                        isLoading: false,
                        isError: false,
                        data: parseInt(
                            new BigNumber(res, 16).div(100_000_000).valueOf()
                        ),
                    })
                }
            })
            setNgmiStakedBalance({
                isLoading: false,
                isError: false,
                data: 1000,
            })
        } else {
            setNgmiStakedBalance({
                isLoading: false,
                isError: false,
                data: null,
            })
        }
    }, [address])

    return ngmiStakedBalance
}

const useNGMIBalance = (address: string) => {
    const [ngmiBalance, setNgmiBalance] = useState<IFetch>(defaultFetch)

    useEffect(() => {
        if (address) {
            getAccountNgmiBalance(address).then((res) => {
                if (res.success && res.data.length > 0) {
                    let denominatedBalance = new BigNumber(res.data[0].balance)
                    let balance = denominatedBalance.dividedBy(
                        new BigNumber(100_000_000)
                    )
                    setNgmiBalance({
                        isLoading: false,
                        isError: false,
                        data: balance.valueOf(),
                    })
                } else {
                    setNgmiBalance({
                        isLoading: false,
                        isError: false,
                        data: '0',
                    })
                }
            })
            setNgmiBalance({
                isLoading: false,
                isError: false,
                data: 10000,
            })
        } else {
            setNgmiBalance({
                isLoading: false,
                isError: false,
                data: null,
            })
        }
    }, [address])

    return ngmiBalance
}

const useClaimableRewards = (address: string) => {
    const [ngmiRewards, setNgmiRewards] = useState<IFetch>(defaultFetch)

    useEffect(() => {
        if (address) {
            fetchQueryHex(
                'claimableRewards',
                [new AddressValue(new Address(address))],
                ngmiStakingSCAddress
            ).then((res) => {
                if (res === '-1') {
                    setNgmiRewards({
                        isLoading: false,
                        isError: false,
                        data: 0,
                    })
                } else {
                    console.log('Res', res)
                    setNgmiRewards({
                        isLoading: false,
                        isError: false,
                        data: new BigNumber(res, 16)
                            .dividedBy(100_000_000)
                            .valueOf(),
                    })
                }
            })
            setNgmiRewards({
                isLoading: false,
                isError: false,
                data: 1000,
            })
        } else {
            setNgmiRewards({
                isLoading: false,
                isError: false,
                data: null,
            })
        }
    }, [address])

    return ngmiRewards
}

const useUnbondingNgmi = (address: string) => {
    const [unbondingNgmi, setUnbondingNgmi] =
        useState<IFetchUnstake>(defaultFetch)

    useEffect(() => {
        if (address) {
            Promise.all([
                fetchQueryHex(
                    'unstakeAmount',
                    [new AddressValue(new Address(address))],
                    ngmiStakingSCAddress
                ),
                fetchQueryHex(
                    'unstakeTime',
                    [new AddressValue(new Address(address))],
                    ngmiStakingSCAddress
                ),
            ]).then(([unstakeAmountRes, unstakeTimeRes]) => {
                if (unstakeAmountRes === '-1' || unstakeTimeRes === '-1') {
                    setUnbondingNgmi({
                        isLoading: false,
                        isError: false,
                        data: {
                            isReady: true,
                            isUnbonding: false,
                            date: null,
                            amount: 0,
                        },
                    })
                } else {
                    const unstakeAmount = new BigNumber(unstakeAmountRes, 16)
                    const unstakeTime = parseInt(unstakeTimeRes, 16)
                    const unstakeDate = new Date(
                        (unstakeTime + 240 * 3600) * 1000
                    )
                    setUnbondingNgmi({
                        isLoading: false,
                        isError: false,
                        data: {
                            isReady: new Date(Date.now()) > unstakeDate,
                            isUnbonding:
                                unstakeAmount.isGreaterThanOrEqualTo(1),
                            date: unstakeDate,
                            amount: parseInt(
                                unstakeAmount.dividedBy(100_000_000).valueOf()
                            ),
                        },
                    })
                }
            })
        } else {
            setUnbondingNgmi({
                isLoading: false,
                isError: false,
                data: null,
            })
        }
    }, [address])

    return unbondingNgmi
}

const ticker = 'NGMI'

const getAPR = (stakedBalance: number) => {
    if (stakedBalance < 1_000_000) return 30
    if (stakedBalance < 1_500_000) return 27.5
    if (stakedBalance < 2_000_000) return 26
    if (stakedBalance < 2_500_000) return 25
    if (stakedBalance < 3_000_000) return 24
    if (stakedBalance < 4_000_000) return 23
    if (stakedBalance < 5_000_000) return 22
    if (stakedBalance < 7_500_000) return 21
    if (stakedBalance < 10_000_000) return 20
    if (stakedBalance < 15_000_000) return 19
    if (stakedBalance < 20_000_000) return 18
    return 16.69
}

const StakingModal = ({ shown, close }: any) => {
    useModalDisableScroll(shown)

    const [isLoading, setIsLoading] = useState(true)
    const [isError, setIsError] = useState(false)
    //To simulate logging in and having some actual data, comment "useGetAccountInfo()" line and uncomment the address line
    const { address } = useGetAccountInfo()
    // const address =
    //     'erd1y6jslrt584hwxczk2hvrezs4pet87936ttqhzx63lmzwsg8gmw2srqxv47'
    //const address =
    //    'erd1tztluf08g90max7jkr4jtac9w5qv7qacgkhh57q9nz2erq9y2p3sd5njkg'
    //const address =
    //    'erd1jx97k3he3kwx3g69nqhhz4kn2ufv0hx0jwch5txvn49gt3wxwheqh62l4r'

    const loggedIn = Boolean(address)

    const {
        data: ngmiBalance,
        isLoading: isNgmiBalanceLoading,
        isError: isNgmiBalanceError,
    } = useNGMIBalance(address)

    const {
        data: ngmiStakedByUser,
        isLoading: isNgmiStakedByUserLoading,
        isError: isNgmiStakedByUserError,
    } = useNGMIStakedBalance(address)

    const {
        data: claimableRewards,
        isLoading: isClaimableRewardsLoading,
        isError: isClaimableRewardsError,
    } = useClaimableRewards(address)

    const {
        data: unbondingNgmi,
        isLoading: isUnbondingNgmiLoading,
        isError: isUnbondingNgmiError,
    } = useUnbondingNgmi(address)

    const [toStake, setToStake] = useState<number>(1)

    useEffect(() => {
        if (ngmiBalance) setToStake(Math.ceil(ngmiBalance / 2))
    }, [ngmiBalance])

    useEffect(() => {
        setIsLoading(
            isNgmiStakedByUserLoading ||
                isClaimableRewardsLoading ||
                isUnbondingNgmiLoading ||
                isNgmiBalanceLoading
        )
    }, [
        isNgmiStakedByUserLoading,
        isClaimableRewardsLoading,
        isUnbondingNgmiLoading,
        isNgmiBalanceLoading,
    ])

    useEffect(() => {
        setIsError(
            isNgmiStakedByUserError ||
                isClaimableRewardsError ||
                isUnbondingNgmiError ||
                isNgmiBalanceError
        )
    }, [
        isNgmiStakedByUserError,
        isClaimableRewardsError,
        isUnbondingNgmiError,
        isNgmiBalanceError,
    ])

    const handleStake: FormEventHandler<HTMLFormElement> = async (e) => {
        e.preventDefault()
        const stakeTransaction = {
            value: '0',
            receiver: ngmiStakingSCAddress,
            gasLimit: 8_000_000,
            data: `ESDTTransfer@${str2hex('NGMI-dd4096')}@${int2hex(
                toStake * 100_000_000
            )}@${str2hex('stake')}`,
        }
        await refreshAccount()

        const { sessionId /*, error*/ } = await sendTransactions({
            transactions: stakeTransaction,
            transactionsDisplayInfo: {
                processingMessage: 'Processing transaction',
                errorMessage: 'An error has occured during staking',
                successMessage: 'Transaction successful',
                transactionDuration: 10000,
            },
        })
        if (sessionId != null) {
            console.log('session id: ' + sessionId)
        }
    }

    const handleClaimRewards = async () => {
        const claimRewardsTransaction = {
            value: '0',
            receiver: ngmiStakingSCAddress,
            gasLimit: 20_000_000,
            data: 'claimRewards',
        }
        await refreshAccount()

        const { sessionId /*, error*/ } = await sendTransactions({
            transactions: claimRewardsTransaction,
            transactionsDisplayInfo: {
                processingMessage: 'Claiming rewards',
                errorMessage: 'An error has occured during claiming rewards',
                successMessage: 'Transaction successful',
                transactionDuration: 10000,
            },
        })
        if (sessionId != null) {
            console.log('session id: ' + sessionId)
        }
    }

    const handleCompoundRewards = async () => {
        const compoundRewardsTransaction = {
            value: '0',
            receiver: ngmiStakingSCAddress,
            gasLimit: 20_000_000,
            data: 'compoundRewards',
        }
        await refreshAccount()

        const { sessionId /*, error*/ } = await sendTransactions({
            transactions: compoundRewardsTransaction,
            transactionsDisplayInfo: {
                processingMessage: 'Compounding rewards',
                errorMessage: 'An error has occured during compounding rewards',
                successMessage: 'Transaction successful',
                transactionDuration: 10000,
            },
        })
        if (sessionId != null) {
            console.log('session id: ' + sessionId)
        }
    }

    const handleUnstake: MouseEventHandler<HTMLButtonElement> = async (e) => {
        e.preventDefault()
        const unstakeTransaction = {
            value: '0',
            receiver: ngmiStakingSCAddress,
            gasLimit: 20_000_000,
            data: 'unstake',
        }
        await refreshAccount()

        const { sessionId /*, error*/ } = await sendTransactions({
            transactions: unstakeTransaction,
            transactionsDisplayInfo: {
                processingMessage: 'Unstaking $NGMI',
                errorMessage: 'An error has occured during unstaking',
                successMessage: 'Transaction successful',
                transactionDuration: 10000,
            },
        })
        if (sessionId != null) {
            console.log('session id: ' + sessionId)
        }
    }

    const handleUnstakeClaim = async () => {
        // TODO: Alex
        const claimUnstakeTransaction = {
            value: '0',
            receiver: ngmiStakingSCAddress,
            gasLimit: 20_000_000,
            data: 'claimUnstake',
        }
        await refreshAccount()

        const { sessionId /*, error*/ } = await sendTransactions({
            transactions: claimUnstakeTransaction,
            transactionsDisplayInfo: {
                processingMessage: 'Claiming unstaked $NGMI',
                errorMessage: 'An error has occured during claiming',
                successMessage: 'Transaction successful',
                transactionDuration: 10000,
            },
        })
        if (sessionId != null) {
            console.log('session id: ' + sessionId)
        }
    }

    const handleLogout: MouseEventHandler<HTMLButtonElement> = (e) => {
        e.preventDefault()
        logout(`${window.location.origin}/`)
    }

    return (
        shown && (
            <>
                <div
                    className="stakingmodal-backdrop"
                    onClick={() => {
                        close()
                    }}
                >
                    <div
                        className="stakingmodal-content"
                        onClick={(e) => {
                            e.stopPropagation()
                        }}
                    >
                        {!loggedIn ? (
                            <LoginComponentInline />
                        ) : isLoading ? (
                            <p>Loading...</p>
                        ) : isError ? (
                            <p>
                                An error occured. Please{' '}
                                <a
                                    href="https://twitter.com/NaturalDegenz"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    contact us on Twitter
                                </a>
                            </p>
                        ) : (
                            <>
                                <div className="ngmi-staking">
                                    <h3>
                                        Stake your {ticker} (
                                        {!ngmiStakedByUser ? 'up to ' : ''}
                                        {getAPR(ngmiStakedByUser)} % APR)
                                    </h3>
                                    <div className="ngmi-staking__container">
                                        <div className="ngmi-staking__item">
                                            <div>Balance</div>
                                            <div>
                                                {ngmiBalance?.toLocaleString()}{' '}
                                                {ticker}
                                            </div>
                                        </div>
                                        {!unbondingNgmi?.isUnbonding && (
                                            <div className="ngmi-staking__item">
                                                <div>Staked</div>
                                                <div>
                                                    {ngmiStakedByUser?.toLocaleString()}{' '}
                                                    {ticker}
                                                </div>
                                                {+ngmiBalance > 0 && (
                                                    <form
                                                        className="w-100"
                                                        onSubmit={handleStake}
                                                    >
                                                        <div className="form-group">
                                                            <label htmlFor="formControlRange">
                                                                Stake:{' '}
                                                                {toStake.toLocaleString()}{' '}
                                                                {ngmiStakedByUser >
                                                                0
                                                                    ? 'more'
                                                                    : ''}{' '}
                                                                {ticker}
                                                            </label>
                                                            <input
                                                                type="range"
                                                                className="form-control-range"
                                                                id="formControlRange"
                                                                min={1}
                                                                max={
                                                                    ngmiBalance
                                                                }
                                                                value={toStake}
                                                                onChange={(e) =>
                                                                    setToStake(
                                                                        +e
                                                                            .target
                                                                            .value
                                                                    )
                                                                }
                                                            />
                                                        </div>
                                                        <button>Stake</button>
                                                    </form>
                                                )}
                                            </div>
                                        )}
                                        {+ngmiStakedByUser > 0 &&
                                            !unbondingNgmi?.isUnbonding && (
                                                <div className="ngmi-staking__item">
                                                    <div>Unstake</div>
                                                    <button
                                                        onClick={handleUnstake}
                                                    >
                                                        Unstake all
                                                    </button>
                                                </div>
                                            )}
                                        <div className="ngmi-staking__item">
                                            <div>Rewards</div>
                                            <div>
                                                {claimableRewards?.toLocaleString()}{' '}
                                                {ticker}
                                            </div>
                                            <div
                                                className="d-flex justify-content-center w-100"
                                                style={{ gap: '1rem' }}
                                            >
                                                <button
                                                    onClick={
                                                        handleCompoundRewards
                                                    }
                                                    disabled={!claimableRewards}
                                                >
                                                    Compound
                                                </button>
                                                <button
                                                    onClick={handleClaimRewards}
                                                    disabled={!claimableRewards}
                                                >
                                                    Claim
                                                </button>
                                            </div>
                                        </div>
                                        {unbondingNgmi?.isUnbonding && (
                                            <div className="ngmi-staking__item">
                                                <div>
                                                    {unbondingNgmi?.isReady
                                                        ? 'Unbonded'
                                                        : 'Unbonding'}{' '}
                                                    {ticker}
                                                </div>
                                                <div>
                                                    {unbondingNgmi?.amount?.toLocaleString()}{' '}
                                                    {ticker}
                                                </div>
                                                <div className="w-100">
                                                    <button
                                                        disabled={
                                                            !unbondingNgmi.isReady
                                                        }
                                                        onClick={
                                                            handleUnstakeClaim
                                                        }
                                                    >
                                                        {unbondingNgmi?.isReady
                                                            ? 'Claim now'
                                                            : `Ready on ${unbondingNgmi?.date?.toLocaleString()}`}
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                        <p className="text-danger mt-4">
                                            Please note that the unstaking
                                            period of your staked NGMI is 10
                                            days.
                                        </p>
                                    </div>
                                </div>

                                <div className="footer">
                                    <button onClick={handleLogout}>
                                        Logout
                                    </button>
                                    <button
                                        onClick={(e) => {
                                            close(e)
                                        }}
                                    >
                                        close
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </>
        )
    )
}

export default StakingModal
